import type { Prisma, PaymentReceipt, PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import { z } from 'zod';
import { HttpError } from '../../core/errors.js';
import { nextDocumentNumber } from '../../core/sequence.js';
import { postEntry, reverseEntry, type PostingLine } from '../accounting/posting.service.js';
import type { AuthContext } from '../auth/auth.types.js';
import { BASE_CURRENCY, outstanding } from './calc.js';
import {
  applyAdvance,
  isoOf,
  logEvent,
  refreshInvoiceStatus,
  toDate,
} from './documents.service.js';

type Tx = Prisma.TransactionClient;
type Db = PrismaClient | Tx;
const dec = (v: Decimal.Value | { toString(): string }) => new Decimal(v.toString());
const baseRound = (v: Decimal) => v.toDecimalPlaces(0, Decimal.ROUND_HALF_UP);

const money = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .refine((v) => /^\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid amount');

export const receiptInputSchema = z.object({
  partyId: z.number().int().positive(),
  receiptDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  financialAccountId: z.number().int().positive(),
  currency: z
    .string()
    .trim()
    .length(3)
    .transform((v) => v.toUpperCase())
    .optional(),
  exchangeRate: money.optional(),
  /** Cash actually received. */
  amount: money.refine((v) => Number(v) > 0, 'Amount must be greater than zero'),
  /** Tax the client withheld from the payment (WHT Receivable). */
  whtWithheld: money.default('0'),
  reference: z.string().trim().max(100).nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
  allocations: z
    .array(
      z.object({
        invoiceId: z.number().int().positive(),
        amount: money.refine((v) => Number(v) > 0),
      }),
    )
    .max(100)
    .default([]),
  /** With no allocations, allocate to the oldest open invoices first. */
  autoAllocate: z.boolean().default(false),
  /** Advance payment against a proforma invoice. */
  proformaId: z.number().int().positive().nullable().optional(),
});
export type ReceiptInput = z.infer<typeof receiptInputSchema>;

export const OPEN_INVOICE_STATUSES = ['approved', 'sent', 'partially_paid'];

export async function createReceipt(
  tx: Tx,
  auth: AuthContext,
  input: ReceiptInput,
  opts: { receiptNo?: string } = {},
): Promise<PaymentReceipt> {
  const party = await tx.party.findFirst({
    where: { id: input.partyId, companyId: auth.companyId, active: true },
  });
  if (!party || !(party.isClient || party.isProspect))
    throw new HttpError(400, 'VALIDATION_ERROR', 'Client not found');
  const account = await tx.financialAccount.findFirst({
    where: { id: input.financialAccountId, companyId: auth.companyId },
  });
  if (!account || account.status !== 'active')
    throw new HttpError(400, 'VALIDATION_ERROR', 'Choose an active bank or payment account');

  const currency = input.currency ?? party.currency;
  const date = toDate(input.receiptDate);
  let rate = new Decimal(1);
  if (currency !== BASE_CURRENCY) {
    if (input.exchangeRate) rate = dec(input.exchangeRate);
    else {
      const latest = await tx.exchangeRate.findFirst({
        where: { companyId: auth.companyId, currency, effectiveDate: { lte: date } },
        orderBy: { effectiveDate: 'desc' },
      });
      if (!latest)
        throw new HttpError(422, 'NO_EXCHANGE_RATE', `Enter the exchange rate for ${currency}`);
      rate = dec(latest.rate);
    }
  }

  const amount = dec(input.amount);
  const wht = dec(input.whtWithheld);
  const settlement = amount.plus(wht); // what the client has effectively paid off
  if (wht.gt(0) && !party.whtAgent) {
    // Allowed (agreements change), but worth recording for the audit trail via the note.
  }

  // Which invoices does this payment settle?
  const allocations = input.allocations.map((a) => ({
    invoiceId: a.invoiceId,
    amount: dec(a.amount),
  }));
  if (allocations.length === 0 && input.autoAllocate && !input.proformaId) {
    const open = await tx.salesDocument.findMany({
      where: {
        companyId: auth.companyId,
        docType: 'invoice',
        partyId: party.id,
        currency,
        status: { in: OPEN_INVOICE_STATUSES },
      },
      orderBy: [{ dueDate: 'asc' }, { id: 'asc' }],
    });
    let left = settlement;
    for (const inv of open) {
      if (left.lte(0)) break;
      const pay = Decimal.min(left, outstanding(inv));
      if (pay.gt(0)) allocations.push({ invoiceId: inv.id, amount: pay });
      left = left.minus(pay);
    }
  }
  const invoices = await tx.salesDocument.findMany({
    where: {
      id: { in: allocations.map((a) => a.invoiceId) },
      companyId: auth.companyId,
      docType: 'invoice',
    },
  });
  const allocated = allocations.reduce((s, a) => s.plus(a.amount), new Decimal(0));
  if (new Set(allocations.map((a) => a.invoiceId)).size !== allocations.length)
    throw new HttpError(
      400,
      'VALIDATION_ERROR',
      'An invoice can appear only once in the allocations',
    );
  if (allocated.gt(settlement))
    throw new HttpError(409, 'OVER_ALLOCATED', 'The allocations are more than the amount received');
  for (const a of allocations) {
    const inv = invoices.find((i) => i.id === a.invoiceId);
    if (!inv) throw new HttpError(404, 'NOT_FOUND', 'Invoice not found');
    if (inv.partyId !== party.id)
      throw new HttpError(
        409,
        'PARTY_MISMATCH',
        `Invoice ${inv.number} belongs to a different client`,
      );
    if (inv.currency !== currency)
      throw new HttpError(
        409,
        'CURRENCY_MISMATCH',
        `Invoice ${inv.number} is in ${inv.currency}; record the receipt in ${inv.currency}`,
      );
    if (!OPEN_INVOICE_STATUSES.includes(inv.status))
      throw new HttpError(
        409,
        'INVOICE_NOT_OPEN',
        `Invoice ${inv.number ?? ''} is ${inv.status.replace('_', ' ')} and cannot receive payments`,
      );
    if (a.amount.gt(outstanding(inv)))
      throw new HttpError(
        409,
        'OVERPAYMENT',
        `Allocation to ${inv.number} is more than the ${outstanding(inv).toString()} owed`,
      );
  }
  if (input.proformaId) {
    const pf = await tx.salesDocument.findFirst({
      where: {
        id: input.proformaId,
        companyId: auth.companyId,
        docType: 'proforma',
        partyId: party.id,
      },
    });
    if (!pf) throw new HttpError(400, 'VALIDATION_ERROR', 'Proforma not found for this client');
  }

  const advance = settlement.minus(allocated); // overpayment or advance
  const receiptNo =
    opts.receiptNo ?? (await nextDocumentNumber(tx, auth.companyId, 'receipt', date));
  const receipt = await tx.paymentReceipt.create({
    data: {
      companyId: auth.companyId,
      receiptNo,
      partyId: party.id,
      receiptDate: date,
      financialAccountId: account.id,
      currency,
      exchangeRate: rate.toString(),
      amount: amount.toString(),
      whtWithheld: wht.toString(),
      reference: input.reference ?? null,
      notes: input.notes ?? null,
      allocatedAmount: allocated.toString(),
      advanceAmount: advance.toString(),
      proformaId: input.proformaId ?? null,
      createdBy: auth.userId,
      allocations: {
        create: allocations.map((a) => ({ invoiceId: a.invoiceId, amount: a.amount.toString() })),
      },
    },
  });

  // Ledger: Dr bank (+ WHT receivable) / Cr AR per invoice (+ customer advances), FX difference last.
  const cashBase = baseRound(amount.times(rate));
  const whtBase = baseRound(wht.times(rate));
  const lines: PostingLine[] = [
    {
      accountId: account.ledgerAccountId,
      debit: cashBase.toFixed(4),
      memo: input.reference ?? undefined,
    },
  ];
  if (whtBase.gt(0))
    lines.push({ systemKey: 'WHT_REC', debit: whtBase.toFixed(4), memo: 'Withheld by client' });
  let credits = new Decimal(0);
  for (const a of allocations) {
    const inv = invoices.find((i) => i.id === a.invoiceId)!;
    const arBase = baseRound(a.amount.times(dec(inv.exchangeRate)));
    credits = credits.plus(arBase);
    lines.push({ systemKey: 'AR', credit: arBase.toFixed(4), memo: inv.number ?? undefined });
  }
  if (advance.gt(0)) {
    const advBase = baseRound(advance.times(rate));
    credits = credits.plus(advBase);
    lines.push({
      systemKey: 'CUST_ADV',
      credit: advBase.toFixed(4),
      memo: input.proformaId ? 'Advance against proforma' : 'Overpayment / advance',
    });
  }
  const diff = cashBase.plus(whtBase).minus(credits);
  if (diff.gt(0))
    lines.push({ systemKey: 'FX_GAIN', credit: diff.toFixed(4), memo: 'Realised exchange gain' });
  if (diff.lt(0))
    lines.push({
      systemKey: 'FX_LOSS',
      debit: diff.abs().toFixed(4),
      memo: 'Realised exchange loss',
    });

  const entry = await postEntry(tx, {
    companyId: auth.companyId,
    date: input.receiptDate,
    description: `Receipt ${receiptNo}: ${party.name}`,
    sourceType: 'receipt',
    sourceId: receipt.id,
    reference: input.reference ?? receiptNo,
    lines,
    userId: auth.userId,
  });

  for (const a of allocations) {
    const inv = invoices.find((i) => i.id === a.invoiceId)!;
    await tx.salesDocument.update({
      where: { id: inv.id },
      data: { amountPaid: dec(inv.amountPaid).plus(a.amount).toString() },
    });
    await refreshInvoiceStatus(tx, inv.id);
    await logEvent(tx, {
      companyId: auth.companyId,
      entityType: 'sales_document',
      entityId: inv.id,
      type: 'payment',
      userId: auth.userId,
      note: `${a.amount.toString()} ${currency} (${receiptNo})`,
    });
  }
  if (advance.gt(0)) {
    await tx.customerAdvance.create({
      data: {
        companyId: auth.companyId,
        partyId: party.id,
        currency,
        exchangeRate: rate.toString(),
        originalAmount: advance.toString(),
        remainingAmount: advance.toString(),
        sourceType: 'receipt',
        sourceId: receipt.id,
        proformaId: input.proformaId ?? null,
      },
    });
  }
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'receipt',
    entityId: receipt.id,
    type: 'created',
    userId: auth.userId,
    note: receiptNo,
  });
  return tx.paymentReceipt.update({
    where: { id: receipt.id },
    data: { journalEntryId: entry.id },
  });
}

/** Use money held as an advance (overpayment, proforma advance, credit-note excess) against invoices. */
export async function applyAdvances(
  tx: Tx,
  auth: AuthContext,
  advanceId: number,
  allocations: { invoiceId: number; amount: string }[],
) {
  for (const a of allocations)
    await applyAdvance(tx, auth, { advanceId, invoiceId: a.invoiceId, amount: dec(a.amount) });
}

export async function voidReceipt(
  tx: Tx,
  auth: AuthContext,
  id: number,
  reason: string,
): Promise<PaymentReceipt> {
  const receipt = await tx.paymentReceipt.findFirst({
    where: { id, companyId: auth.companyId },
    include: { allocations: true },
  });
  if (!receipt) throw new HttpError(404, 'NOT_FOUND', 'Receipt not found');
  if (receipt.status === 'void')
    throw new HttpError(409, 'ALREADY_VOID', 'This receipt is already void');
  const advance = await tx.customerAdvance.findFirst({
    where: { sourceType: 'receipt', sourceId: receipt.id },
  });
  if (advance && !dec(advance.remainingAmount).equals(dec(advance.originalAmount))) {
    throw new HttpError(
      409,
      'ADVANCE_USED',
      'Part of the advance from this receipt has already been applied to invoices. Void those applications first.',
    );
  }
  const reversal = await reverseEntry(tx, {
    companyId: auth.companyId,
    entryId: receipt.journalEntryId!,
    reason: `Void ${receipt.receiptNo}: ${reason}`,
    date: new Date().toISOString().slice(0, 10),
    userId: auth.userId,
  });
  for (const a of receipt.allocations) {
    const inv = await tx.salesDocument.findUniqueOrThrow({ where: { id: a.invoiceId } });
    await tx.salesDocument.update({
      where: { id: inv.id },
      data: { amountPaid: Decimal.max(0, dec(inv.amountPaid).minus(a.amount)).toString() },
    });
    await refreshInvoiceStatus(tx, inv.id);
    await logEvent(tx, {
      companyId: auth.companyId,
      entityType: 'sales_document',
      entityId: inv.id,
      type: 'payment_voided',
      userId: auth.userId,
      note: receipt.receiptNo,
    });
  }
  if (advance) await tx.customerAdvance.delete({ where: { id: advance.id } });
  const updated = await tx.paymentReceipt.update({
    where: { id },
    data: {
      status: 'void',
      voidedAt: new Date(),
      voidReason: reason.slice(0, 300),
      voidEntryId: reversal.id,
    },
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'receipt',
    entityId: id,
    type: 'void',
    userId: auth.userId,
    note: reason,
  });
  return updated;
}

export async function receiptView(
  db: Db,
  r: PaymentReceipt & { allocations?: { id: number; invoiceId: number; amount: Prisma.Decimal }[] },
) {
  const party = await db.party.findUnique({ where: { id: r.partyId }, select: { name: true } });
  const account = await db.financialAccount.findUnique({
    where: { id: r.financialAccountId },
    select: { name: true },
  });
  const invoices = r.allocations?.length
    ? await db.salesDocument.findMany({
        where: { id: { in: r.allocations.map((a) => a.invoiceId) } },
        select: { id: true, number: true },
      })
    : [];
  return {
    id: r.id,
    receiptNo: r.receiptNo,
    partyId: r.partyId,
    partyName: party?.name,
    receiptDate: isoOf(r.receiptDate),
    financialAccountId: r.financialAccountId,
    accountName: account?.name,
    currency: r.currency,
    exchangeRate: r.exchangeRate.toString(),
    amount: r.amount.toFixed(4),
    whtWithheld: r.whtWithheld.toFixed(4),
    reference: r.reference,
    notes: r.notes,
    allocatedAmount: r.allocatedAmount.toFixed(4),
    advanceAmount: r.advanceAmount.toFixed(4),
    proformaId: r.proformaId,
    status: r.status,
    voidReason: r.voidReason,
    sentAt: r.sentAt,
    createdAt: r.createdAt,
    allocations: (r.allocations ?? []).map((a) => ({
      id: a.id,
      invoiceId: a.invoiceId,
      invoiceNumber: invoices.find((i) => i.id === a.invoiceId)?.number ?? null,
      amount: a.amount.toFixed(4),
    })),
  };
}
